<script setup>
import {computed, onMounted} from 'vue'
import {useFormsStore} from '@/stores/forms'
import {useFormStore} from '@/stores/form'
import Form from '@/common/form/Form.vue'
import FormSaveBtn from '@/common/form/FormSaveBtn.vue'

const props = defineProps({
    modal: {type: Object, required: true},
    options: {type: Object, required: true}
})

const emit = defineEmits(['hide-modal'])

const formsStore = useFormsStore()

// --- State Setup ---

const tableName = props.options.tableName
const formId = `form:${tableName}:0`
const formStore = useFormStore(formId)

// --- Computed ---

const title = computed(() => {
    return props.options.title || `Add ${tableName.replace(/_/g, ' ')}`
})

/**
 * Safety check: Does the store for this form exist and finish initializing?
 * This prevents crashes in both <Form> and <FormSaveBtn>.
 */
const formExists = computed(() => {
    return !!formStore.fields && formStore.fields.length > 0
})

// --- Methods ---

const createForm = () => {
    // Link the formId to the modal for unsaved change detection
    // eslint-disable-next-line vue/no-mutating-props
    props.modal.formId = formId

    formsStore.createForm({
        tableName: tableName,
        id: 0,
        // The formsStore.createForm action handles the store initialization
        onSave: (obj) => {
            if (props.options.onAddItem) {
                props.options.onAddItem(obj)
            }

            // Allow the save button to finish its 'Success' state animation
            setTimeout(() => {
                emit('hide-modal')
            }, 100)
        }
    })
}

// --- Lifecycle ---

onMounted(() => {
    createForm()
})
</script>

<template>
    <div class="modal-dialog modal-lg">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{{ title }}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    @click="$emit('hide-modal')"
                ></button>
            </div>

            <div class="modal-body">
                <Form
                    v-if="formExists"
                    :id="0"
                    :form-id="formId"
                />

                <div v-else class="p-5 text-center">
                    <div class="spinner-border text-secondary" role="status"></div>
                    <p class="mt-2 text-muted small">Initializing form...</p>
                </div>
            </div>

            <div class="modal-footer">
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="$emit('hide-modal')"
                >
                    Cancel
                </button>

                <FormSaveBtn
                    v-if="formExists"
                    :form-id="formId"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
.modal-content {
    border-radius: 0.5rem;
    border: none;
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}
</style>